import React, { useRef } from 'react';
import { Form, Button } from 'react-bootstrap';
import { FaImage, FaTrash, FaCloudUploadAlt, FaStar } from 'react-icons/fa';
import './UpdateMediaUploadSection.scss';

interface UpdateMediaUploadSectionProps {
  imageFiles: File[];
  handleImageFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemoveImage: (index: number) => void;
  imageList: string[];
  mainImage: string;
  setMainImage: (image: string) => void;
  imagesToDelete: string[];
  setImagesToDelete: (images: string[]) => void;
}

const UpdateMediaUploadSection: React.FC<UpdateMediaUploadSectionProps> = ({
  imageFiles,
  handleImageFileChange,
  handleRemoveImage,
  imageList,
  mainImage,
  setMainImage,
  imagesToDelete,
  setImagesToDelete
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDropZoneClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleImageFileChange({
        target: { files }
      } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const handleDeleteExistingImage = (image: string) => {
    setImagesToDelete([...imagesToDelete, image]);
  };

  const handleSetMainImage = (image: string) => {
    setMainImage(image);
  };

  const getImageUrl = (image: string) => {
    // Assuming images are stored with a base URL
    return image.startsWith('http') ? image : `${process.env.REACT_APP_API_URL || ''}/uploads/${image}`;
  };

  // Filter out deleted images from display
  const displayImageList = imageList.filter(img => !imagesToDelete.includes(img));

  return (
    <div className="update-media-upload-section">
      <div className="section-header">
        <h6 className="section-title">
          <FaImage className="section-icon" />
          Imágenes
        </h6>
        <p className="section-description">
          Subir imágenes del producto (primera imagen será la principal)
        </p>
      </div>

      <div className="upload-content">
        {/* Existing Images */}
        {displayImageList.length > 0 && (
          <div className="existing-images">
            <h6 className="subsection-title">Imágenes Actuales</h6>
            <div className="image-grid">
              {displayImageList.map((image, index) => (
                <div key={index} className="image-item existing-image">
                  <img
                    src={getImageUrl(image)}
                    alt={`Imagen ${index + 1}`}
                    className="image-preview"
                  />
                  <div className="image-actions">
                    <Button
                      variant="none"
                      size="sm"
                      className={`set-main-btn ${image === mainImage ? 'active' : ''}`}
                      onClick={() => handleSetMainImage(image)}
                      title={image === mainImage ? "Imagen principal" : "Establecer como principal"}
                    >
                      <FaStar />
                    </Button>
                    <Button
                      variant="none"
                      size="sm"
                      className="delete-btn"
                      onClick={() => handleDeleteExistingImage(image)}
                      title="Eliminar imagen"
                    >
                      <FaTrash />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* New Images */}
        {imageFiles.length > 0 && (
          <div className="new-images">
            <h6 className="subsection-title">Nuevas Imágenes</h6>
            <div className="image-grid">
              {imageFiles.map((file, index) => (
                <div key={index} className="image-item new-image">
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`Nueva imagen ${index + 1}`}
                    className="image-preview"
                  />
                  <div className="image-actions">
                    <Button
                      variant="none"
                      size="sm"
                      className="delete-btn"
                      onClick={() => handleRemoveImage(index)}
                      title="Remover imagen"
                    >
                      <FaTrash />
                    </Button>
                  </div>
                  <div className="image-info">
                    <span className="image-name">{file.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upload Zone */}
        <div 
          className="upload-zone"
          onClick={handleDropZoneClick}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <div className="upload-content">
            <FaCloudUploadAlt className="upload-icon" />
            <p className="upload-text">
              Arrastra archivos aquí o <span className="upload-link">haz clic para seleccionar</span>
            </p>
            <p className="upload-hint">
              Formatos soportados: JPG, PNG, GIF (máx. 5MB)
            </p>
          </div>
        </div>

        <Form.Control
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleImageFileChange}
          style={{ display: 'none' }}
        />
      </div>
    </div>
  );
};

export default UpdateMediaUploadSection;
